import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { FieldDescription } from "@repo/ui/field/fieldDescription";
import { Size } from "@repo/ui/helpers/helpers";
import { linkStyle } from "@repo/ui/Link/index";
import { useId, useState, type FC, type ReactNode } from "react";
import { twMerge } from "tailwind-merge";
import { LazyProseEditor } from "./lazyProseEditor.js";

export type FieldView = "edit" | "diff";

interface FieldDiffState {
  /** The field's value differs from what it is diffed against. */
  hasChanges: boolean;
  /** `diff` only while the toggle is on and there is something to compare. */
  view: FieldView;
  onToggle: () => void;
}

/**
 * Whether a field shows its text or the diff. It goes back to the text when
 * there's nothing to diff, or when `originalValue` changes.
 */
export function useFieldDiffState(
  originalValue: string | undefined,
  value: string,
): FieldDiffState {
  const [view, setView] = useState<FieldView>("edit");
  const [comparedValue, setComparedValue] = useState(originalValue);
  const hasChanges = originalValue !== undefined && originalValue !== value;
  if (originalValue !== comparedValue) {
    setComparedValue(originalValue);
    setView("edit");
  } else if (!hasChanges && view === "diff") {
    setView("edit");
  }
  return {
    hasChanges,
    view: hasChanges ? view : "edit",
    onToggle: () => {
      setView((current) => (current === "edit" ? "diff" : "edit"));
    },
  };
}

interface FieldDiffToggleProps {
  view: FieldView;
  onToggle: () => void;
  /** Labels the way back "Edit" instead of "Hide changes". */
  isEditable: boolean;
}

export const FieldDiffToggle: FC<FieldDiffToggleProps> = (props) => {
  const { view, onToggle, isEditable } = props;
  let label = "See changes";
  if (view === "diff") {
    label = isEditable ? "Edit" : "Hide changes";
  }
  return (
    <Button
      variant={ButtonVariant.Tertiary}
      size={Size.MEDIUM}
      className={twMerge("ml-auto cursor-pointer pr-0", linkStyle())}
      onClick={onToggle}
    >
      {label}
    </Button>
  );
};

export interface DiffableFieldProps {
  label: ReactNode;
  value: string;
  /** Not called when the change comes from the `value` prop. */
  onChange: (value: string) => void;
  /** Compared exactly; callers handle trimming. */
  originalValue: string | undefined;
  /** A pending Ask Alpharun suggestion, shown read-only. */
  suggestedValue?: string;
  placeholder?: string;
  maxLength?: number;
  isReadOnly?: boolean;
  /** @default "textarea" */
  variant?: "input" | "textarea";
  /** Hides long unchanged stretches of the diff behind a bar. */
  collapseUnchanged?: boolean;
  /** Controls the field's view. When set, the view toggle doesn't render. */
  view?: FieldView;
  /** Help text below the editor. */
  description?: ReactNode;
  /** Classes for the editor's box, which the diff shares. */
  editorClassName?: string;
}

/**
 * A labeled text field. "See changes" diffs it against `originalValue`, or a
 * suggestion against it.
 */
export const DiffableField: FC<DiffableFieldProps> = (props) => {
  const {
    label,
    value,
    onChange,
    originalValue,
    suggestedValue,
    placeholder,
    maxLength,
    isReadOnly = false,
    variant,
    collapseUnchanged,
    view,
    description,
    editorClassName,
  } = props;
  const labelId = useId();
  const descriptionId = useId();
  // A suggestion is diffed from the value; the value from the original.
  const diffFrom = suggestedValue === undefined ? originalValue : value;
  const diffTo = suggestedValue ?? value;
  const diff = useFieldDiffState(diffFrom, diffTo);
  const effectiveView =
    view !== undefined && diff.hasChanges ? view : diff.view;
  const isEditable = !isReadOnly && suggestedValue === undefined;

  return (
    <>
      <div className="mb-1 flex min-h-8 items-center justify-between">
        <div id={labelId}>{label}</div>
        {diff.hasChanges && view === undefined ? (
          <FieldDiffToggle
            view={diff.view}
            onToggle={diff.onToggle}
            isEditable={isEditable}
          />
        ) : null}
      </div>
      <LazyProseEditor
        value={diffTo}
        onChange={onChange}
        originalValue={effectiveView === "diff" ? diffFrom : undefined}
        collapseUnchanged={collapseUnchanged}
        variant={variant}
        isReadOnly={!isEditable}
        placeholder={placeholder}
        maxLength={maxLength}
        aria-labelledby={labelId}
        aria-describedby={description ? descriptionId : undefined}
        // Grey when the caller makes it read-only. A suggestion is read-only
        // too, but stays white.
        className={twMerge(
          isReadOnly && effectiveView === "edit" && "bg-slate-50",
          editorClassName,
        )}
      />
      {description ? (
        <FieldDescription id={descriptionId} className="mt-2">
          {description}
        </FieldDescription>
      ) : null}
    </>
  );
};
